import React, { useState, useEffect } from "react";
import notificationService from "../../redux/notification/notificationService";
import RequestsTable from "../../components/AllRequestsTable/RequestsTable";
import api from "../../config/apiConfig";
import AppLoader from "../../components/AppLoader/AppLoader";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import toast, { Toaster } from "react-hot-toast";
import { LuX } from "react-icons/lu";
import {
  OFF_WHITE,
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
} from "../../constants/layout";

const PROFILE_FONT = '"Inter", sans-serif';
const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";
const ICON_BLUE = INPUT_BORDER_FOCUS;

interface Notification {
  id: number;
  notifyType: number;
  notifyTitle: string;
  notifyMessage: string;
  createdAt: string;
  userName?: string;
  requestStatus?: number;
  updateFor?: {
    updateColumn: string;
    oldValue: string | number;
    updateValue: string | number;
  }[];
}

const Requests: React.FC = () => {
  const [requests, setRequests] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [selectedData, setSelectedData] = useState<Notification | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const response = await notificationService.notification({
          notifyType: 3,
          pageIndex: page + 1,
          pageSize: rowsPerPage,
        });

        const filteredRequests = response.data.data.filter(
          (request: Notification) => request.notifyType === 3
        );

        setRequests(filteredRequests);
        setTotalPages(response.data.totalPages);
        setError(null);
      } catch (err) {
        setError("Failed to fetch notifications");
        setRequests([]);
        console.error("Failed to fetch notifications", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [page, rowsPerPage]);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleChangeStatus = async (requestId: number, status: number) => {
    try {
      setLoading(true);
      await api.post(
        "admin/updateRequest/changeStatus",
        { requestId, status },
        { headers: { Authorization: localStorage.getItem("accessToken") } }
      );
      window.location.reload();
    } catch (err) {
      console.error("Failed to change status", err);
      toast.error("Failed to change status. Reloading the page.");
      window.location.reload();
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetails = async (id: number) => {
    try {
      const response = await notificationService.getNotificationById({ id });
      setSelectedData(response.data);
      setDialogOpen(true);
    } catch (err) {
      console.error("Failed to fetch notification by ID:", err);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedData(null);
  };

  const renderValueChanges = (
    data: Notification["updateFor"],
    type: "old" | "new"
  ) => {
    if (!data?.length) return "No changes made";

    const hasChanges = data.some((v) => v.oldValue !== v.updateValue);
    if (!hasChanges) return "No changes made";

    if (type === "old") {
      const removed = data
        .filter((v) => v.oldValue === 1 && v.updateValue === 0)
        .map((v) => v.updateColumn);
      const changed = data
        .filter(
          (v) =>
            v.oldValue !== v.updateValue &&
            v.oldValue !== 1 &&
            v.oldValue !== 0
        )
        .map((v) => v.oldValue);
      const parts = [...removed, ...changed.map(String)];
      if (!parts.length) return "No changes made";
      return `${parts.join(", ")}${removed.length ? " removed" : ""}`;
    }

    const added = data
      .filter((v) => v.oldValue === 0 && v.updateValue === 1)
      .map((v) => v.updateColumn);
    const changed = data
      .filter(
        (v) =>
          v.oldValue !== v.updateValue &&
          v.updateValue !== 1 &&
          v.oldValue !== 1
      )
      .map((v) => v.updateValue);
    const parts = [...added, ...changed.map(String)];
    if (!parts.length) return "No changes made";
    return `${parts.join(", ")}${added.length ? " added" : ""}`;
  };

  return (
    <>
      <AppLoader loading={loading} />
      <RequestsTable
        requests={requests}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        loading={loading}
        error={error}
        onChangeStatus={handleChangeStatus}
        onOpenDetails={handleOpenDetails}
      />

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            fontFamily: PROFILE_FONT,
            boxShadow: CARD_SHADOW,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: PROFILE_FONT,
            fontWeight: 700,
            fontSize: 20,
            color: DARK_NAVY,
            pr: 6,
            pb: 1,
          }}
        >
          Request Details
          <IconButton
            onClick={handleCloseDialog}
            aria-label="Close"
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: DARK_NAVY_LIGHT,
              "&:hover": { bgcolor: OFF_WHITE, color: ICON_BLUE },
            }}
          >
            <LuX size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
          {selectedData ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {[
                { label: "User Name", value: selectedData.userName },
                { label: "Message", value: selectedData.notifyMessage },
                { label: "Old Values", value: renderValueChanges(selectedData.updateFor, "old") },
                { label: "New Values", value: renderValueChanges(selectedData.updateFor, "new") },
                {
                  label: "Created At",
                  value: new Date(selectedData.createdAt || "").toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  }),
                },
              ].map((row) => (
                <Box key={row.label}>
                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, fontWeight: 600, color: DARK_NAVY_LIGHT, mb: 0.5 }}>
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY, lineHeight: 1.5 }}>
                    {row.value || "-"}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontFamily: PROFILE_FONT, color: DARK_NAVY_LIGHT }}>
              No details available
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              fontFamily: PROFILE_FONT,
              textTransform: "none",
              color: DARK_NAVY_LIGHT,
              fontWeight: 500,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Toaster />
    </>
  );
};

export default Requests;
